import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { Log } from '../../AuxillaryService/Log';
import {
    ScoresData,
    ScoresTemplateData,
    ScoresTemplateLayout,
    ScoresTemplateSizeTheme,
    ScoresTemplateSizingData,
    ScoresWidgetConfiguration,
    ScoresWidgetConfigurationDetails,
    ScoresWidgetTimelineSlot,
    ZodScoresWidgetConfigurationDetails,
} from './types';
import { FadeInCommand, FadeOutCommand, WidgetType } from '../types';
import { Signage } from '../../Signage';
import { TemplateWidgetMixin } from '../../Template';
import { getZoneById } from '../../Display';
import { getScores } from '../../ReachApiV1';

export class ScoresWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    type: WidgetType.SCORES = WidgetType.SCORES;
    declare config: ScoresWidgetConfigurationDetails;

    /**
     * Number of seconds between each time the `Scheduler` runs
     */
    schedulerRunInterval: number;

    currentScoreIndex: number;
    width: number;
    height: number;

    scoresArray: ScoresData[];

    /**
     * A flag to let the widget know that it received fresh data and needs to
     * rerender during beforeFadeIn.
     */
    hasUpdates: boolean;

    constructor(widgetConfiguration: ScoresWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.schedulerRunInterval = 900; //15 minutes

        this.currentScoreIndex = 0;

        const zoneInfo = getZoneById(zoneId).returnZoneInfo();

        this.width = zoneInfo?.w || 0;

        this.height = zoneInfo?.h || 0;

        this.scoresArray = [];

        this.hasUpdates = false;
    }

    protected validateConfigurationDetails(
        maybeScoresWidgetConfigurationDetails: ScoresWidgetConfigurationDetails
    ): ScoresWidgetConfigurationDetails {
        return ZodScoresWidgetConfigurationDetails.parse(maybeScoresWidgetConfigurationDetails);
    }

    async initialize(): Promise<void> {
        await this.loadApiData();
    }

    async getReadyForDisplay(): Promise<void> {
        await this.renderContent();
    }

    async schedulerAction(): Promise<void> {
        await this.loadApiData();
    }

    async beforeFadeIn(
        currentSlot: ScoresWidgetTimelineSlot
    ): Promise<Partial<FadeInCommand> | void> {
        // Hide scores is called before rendering replaces the nodes due to a buggy interaction
        // with fullscreen announcements.
        // todo: Expand on explanation and/or fix issues will fullscreen announcements
        this.hideScores();

        if (this.hasUpdates) {
            await this.renderContent();
        }

        // ATTENTION - It is possible for this slot to be out of date.  If newer data has come in,
        // and this gameid has fallen off the feed, there is a window where the rendered data
        // does not contain the element this slot is looking for.
        this.showScore(currentSlot.params.score.gameid);

        // If the data has changed, abort the loop to ensure the rendered content matches
        // the fresh data.
        if (this.hasUpdates) {
            this.hasUpdates = false;

            return {
                abortLoop: true,
            };
        }
    }

    beforeFadeOut(): Partial<FadeOutCommand> {
        //Do not cancel the fade in/out even if it is the same widget
        return {
            cancelFade: false,
        };
    }

    afterFadeOut(currentSlot: ScoresWidgetTimelineSlot): void {
        try {
            this.hideScore(currentSlot.params.score.gameid);
        } catch (error) {
            Log.Error(`Failed to unload scores widget: ${this.id}`, null, __fileline, error);
        }
    }

    /**
     * determines slots to send to timeline
     */
    querySlots(numSlots: number, maxDuration: number): ScoresWidgetTimelineSlot[] {
        this.hasContentToPlay = true;
        if (this.scoresArray.length === 0) {
            this.hasContentToPlay = false;
            return [];
        }

        const slots = [];
        for (let i = 0; i < numSlots; ++i) {
            if (this.duration <= maxDuration) {
                const params = {
                    thisloopcount: this.currentScoreIndex,
                    score: this.scoresArray[this.currentScoreIndex],
                };
                const slot = this.generateSlot(this.duration, undefined, params);
                slots.push(slot);

                maxDuration -= this.duration;
            } else {
                break;
            }

            this.currentScoreIndex++;
            if (this.currentScoreIndex > this.scoresArray.length - 1) {
                this.currentScoreIndex = 0;
            }
        }

        return slots;
    }

    /**
     * determines slots to send to timeline
     */
    querySlotsLinear(timeBased: boolean): ScoresWidgetTimelineSlot[] {
        this.hasContentToPlay = true;
        if (this.scoresArray.length === 0) {
            this.hasContentToPlay = false;
            return [];
        }

        const slots = [];
        if (timeBased) {
            let usedTime = 0;

            do {
                const params = {
                    thisloopcount: this.currentScoreIndex,
                    score: this.scoresArray[this.currentScoreIndex],
                };
                const slot = this.generateSlot(this.duration, undefined, params);
                slots.push(slot);

                //if the next item is undefined, set currentScoreIndex to 0
                this.currentScoreIndex++;
                if (this.currentScoreIndex > this.scoresArray.length - 1) {
                    this.currentScoreIndex = 0;
                }
                usedTime += this.duration;
            } while (usedTime < this.minimumLoopTime);
        } else {
            for (let i = 0; i < this.scoresArray.length; i++) {
                const params = {
                    thisloopcount: this.currentScoreIndex,
                    score: this.scoresArray[this.currentScoreIndex],
                };
                const slot = this.generateSlot(this.duration, undefined, params);
                slots.push(slot);

                this.currentScoreIndex++;
                if (this.currentScoreIndex > this.scoresArray.length - 1) {
                    this.currentScoreIndex = 0;
                }
            }
        }

        return slots;
    }

    /**
     * Hides all score elements
     */
    hideScores(): void {
        const eventHolder: HTMLElement | null = this.wrapperElement.querySelector(
            `#eventsholder_${this.id}`
        );

        if (eventHolder) {
            Array.from(eventHolder.children).forEach((eventChild: Element) => {
                if (eventChild instanceof HTMLElement) {
                    eventChild.style.visibility = 'hidden';
                }
            });
        } else {
            Log.Warning(
                `Unable to hide score elements for Scores Widget ${this.id}`,
                null,
                __fileline
            );
        }
    }

    /**
     * Hide score element by gameId
     */
    hideScore(gameId: string | number): void {
        const eventScoreElement: HTMLElement | null = this.wrapperElement.querySelector(
            `#content-story-${this.id}-${gameId}`
        );

        if (eventScoreElement) {
            eventScoreElement.style.left = '-9999999px';
            eventScoreElement.style.visibility = 'hidden';
        } else {
            Log.Warning(
                `Unable to hide scores for event ${gameId} for Scores Widget ${this.id}`,
                null,
                __fileline
            );
        }
    }

    /**
     * Show score element by gameId
     */
    showScore(gameId: string | number): void {
        const eventScoreElement: HTMLElement | null = this.wrapperElement.querySelector(
            `#content-story-${this.id}-${gameId}`
        );

        if (eventScoreElement) {
            eventScoreElement.style.left = '0px';
            eventScoreElement.style.visibility = 'visible';
        } else {
            Log.Warning(
                `Unable to show scores for event ${gameId} for Scores Widget ${this.id}`,
                null,
                __fileline
            );
        }
    }

    /**
     * @todo: document why the template needs these bits of data
     * @todo: document magic numbers
     */
    static getSizingData(width: number, height: number): ScoresTemplateSizingData {
        const sizingObject = {
            width: width,
            height: height,
            orientation:
                width >= height ? ScoresTemplateLayout.LANDSCAPE : ScoresTemplateLayout.PORTRAIT,
            theme: ScoresTemplateSizeTheme.MEDIUM,
            shrinkFactor: false,
            topPadding: Math.ceil(height / 10),
        };

        const widthByHeightRatio = width / height;
        if (width >= height) {
            if (width <= 500) {
                sizingObject.theme = ScoresTemplateSizeTheme.SMALL;
            } else if (width <= 750) {
                sizingObject.theme = ScoresTemplateSizeTheme.MEDIUM;
            } else {
                sizingObject.theme = ScoresTemplateSizeTheme.LARGE;
            }
            if (widthByHeightRatio > 1.85) {
                sizingObject.shrinkFactor = true;
                if (height <= 400) {
                    sizingObject.theme = ScoresTemplateSizeTheme.SMALL;
                }
                if (height <= 200) {
                    sizingObject.theme = ScoresTemplateSizeTheme.XSMALL;
                }
            }
        } else {
            if (height <= 600) {
                sizingObject.theme = ScoresTemplateSizeTheme.SMALL;
            } else if (height <= 800) {
                sizingObject.theme = ScoresTemplateSizeTheme.MEDIUM;
            } else {
                sizingObject.theme = ScoresTemplateSizeTheme.LARGE;
            }
            if (widthByHeightRatio > 0.8) {
                sizingObject.shrinkFactor = true;
                if (height <= 800) {
                    sizingObject.theme = ScoresTemplateSizeTheme.MEDIUM;
                }
                if (height <= 600) {
                    sizingObject.theme = ScoresTemplateSizeTheme.SMALL;
                }
                if (height <= 400) {
                    sizingObject.theme = ScoresTemplateSizeTheme.XSMALL;
                }
            }
        }
        return sizingObject;
    }

    /**
     * Loads feed objects from the API
     */
    async loadApiData(): Promise<void> {
        try {
            const response = await getScores(this.id, String(Signage.localTimeZone));

            //Since we aren't using change keys, we assume that the data has changed every time.
            this.scoresArray.length = 0; //Empty the stale data
            this.scoresArray.push(
                ...response.feed.map((f) => {
                    return {
                        id: -1,
                        awaylogo: f.awaylogo,
                        awayscore: f.awayscore,
                        awayteam: f.awayteam,
                        awayteamid: f.awayteamid,
                        gameid: f.gameid,
                        homelogo: f.homelogo,
                        homescore: f.homescore,
                        hometeam: f.hometeam,
                        hometeamid: f.hometeamid,
                        league: f.league,
                        status: f.status,
                    };
                })
            );
            this.hasUpdates = true;
        } catch (error) {
            Log.Warning(
                `Failed to get RSS Feed for the Content Widget: ${this.id}`,
                null,
                __fileline,
                error
            );

            throw error;
        }
    }

    /**
     * Generate the data object which is used to build template layout
     */
    getRenderContext(): ScoresTemplateData {
        return {
            wid: this.id.toString(),
            score: { type: this.type },
            scores: this.scoresArray,
            dimensions: ScoresWidget.getSizingData(this.width, this.height),
            wobj: { widget: { widgetsid: this.id } },
            Widget: { Id: this.id },
        };
    }
}
